<script lang="ts">
    import { onMount, onDestroy } from "svelte";
    import { t } from "$lib/i18n/translations";
    import { hapticSwitch } from "$lib/haptics";
    
    import IconBrandGoogleDrive from "@tabler/icons-svelte/IconBrandGoogleDrive.svelte";
    import IconLogin from "@tabler/icons-svelte/IconLogin.svelte";
    import IconLogout from "@tabler/icons-svelte/IconLogout.svelte";
    import IconAlertTriangle from "@tabler/icons-svelte/IconAlertTriangle.svelte";

    const CLIENT_ID = "238666546947-j2idcgqdhht0clv88u8fs76raknjl3fc.apps.googleusercontent.com";
    const SCOPES = "https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.readonly profile email";

    let isConnected = $state(false); 
    let isLoading = $state(false);
    let isGoogleReady = $state(false);
    let adblockWarning = $state(false);
    let userName = $state("");
    let tokenClient: any = null;
    let radarInterval: any;
    let timeoutSeguro: any; // NUEVO: El seguro antibloqueo

    onMount(() => {
        // RADAR: Busca a Google cada 500ms hasta que aparezca
        radarInterval = setInterval(() => {
            // @ts-ignore
            if (typeof window !== "undefined" && window.google && window.google.accounts) {
                isGoogleReady = true;
                adblockWarning = false;
                clearInterval(radarInterval);
                console.log("Motor de Google detectado por el radar.");
            }
        }, 500);

        // Si pasan 4 segundos y no está, lanzamos el aviso de bloqueo
        setTimeout(() => {
            if (!isGoogleReady) {
                adblockWarning = true;
                console.error("Brave / Bloqueador está destruyendo el script de Google.");
            }
        }, 4000);

        const savedToken = localStorage.getItem("gdrive_token");
        const savedName = localStorage.getItem("gdrive_name");
        
        if (savedToken) {
            isConnected = true;
            userName = savedName || "1312sounds_robot";
        }
    });

    onDestroy(() => {
        if (radarInterval) clearInterval(radarInterval);
        if (timeoutSeguro) clearTimeout(timeoutSeguro); // Limpiamos el seguro
    });

    const fetchUserInfo = async (token: string) => {
        try {
            const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.name) {
                userName = data.name;
                localStorage.setItem("gdrive_name", data.name);
            }
        } catch (e) {
            console.error("No se pudo obtener el nombre", e);
        }
    };

    const handleAuthAction = async () => {
        hapticSwitch(); 
        
        if (!isGoogleReady) {
            alert("Brave sigue bloqueando a Google. Lee las instrucciones para activar los botones de inicio de sesión de terceros.");
            return;
        }

        if (!isConnected) {
            isLoading = true;

            // NUEVO: Seguro de vida. Si en 2 mins no hay respuesta, abortamos para no girar al infinito.
            timeoutSeguro = setTimeout(() => {
                if (isLoading) {
                    isLoading = false;
                    console.error("TIMED OUT: Google nunca devolvió la respuesta. Posible bloqueo de Cross-Origin (COOP) de Cobalt.");
                }
            }, 120000);

            // @ts-ignore
            tokenClient = window.google.accounts.oauth2.initTokenClient({
                client_id: CLIENT_ID,
                scope: SCOPES,
                // NUEVO: Chivato de errores directos de Google
                error_callback: (error: any) => {
                    console.error("ERROR DE GOOGLE GSI:", error);
                    isLoading = false;
                    clearTimeout(timeoutSeguro);
                },
                callback: (tokenResponse: any) => {
                    clearTimeout(timeoutSeguro);
                    console.log("RESPUESTA CRUDA DE GOOGLE:", tokenResponse); // NUEVO: Nuestro micrófono oculto

                    if (tokenResponse && tokenResponse.access_token) {
                        console.log("¡Token obtenido con éxito!");
                        localStorage.setItem("gdrive_token", tokenResponse.access_token);
                        isConnected = true;
                        isLoading = false;
                        fetchUserInfo(tokenResponse.access_token);
                    } else {
                        console.error("Google respondió, pero SIN TOKEN.");
                        isLoading = false;
                    }
                },
            });

            tokenClient.requestAccessToken();

        } else {
            isLoading = true;
            const token = localStorage.getItem("gdrive_token");
            if (token) {
                // @ts-ignore
                window.google.accounts.oauth2.revoke(token, () => {
                    localStorage.removeItem("gdrive_token");
                    localStorage.removeItem("gdrive_name");
                    isConnected = false;
                    userName = "";
                    isLoading = false;
                });
            } else {
                localStorage.removeItem("gdrive_token");
                isConnected = false;
                isLoading = false;
            }
        }
    };
</script>

<svelte:head>
    <!-- Etiqueta nativa limpia -->
    <script src="https://accounts.google.com/gsi/client" async defer></script>
</svelte:head>

<div id="gdrive-auth-container">
    <div class="auth-card">
        <div class="card-header">
            <IconBrandGoogleDrive class="drive-logo" />
            <div class="header-text">
                <h3>Servicio de Nube</h3>
                <p>Configura tu acceso a Google Drive para envíos directos.</p>
            </div>
        </div>

        {#if adblockWarning}
            <div class="warning-box">
                <IconAlertTriangle />
                <div style="display:flex; flex-direction:column; gap:2px;">
                    <span><b>Bloqueo Global de Brave activado.</b></span>
                    <span style="font-weight: 400;">Ve a <code style="background:rgba(0,0,0,0.3); padding:2px 4px; border-radius:4px;">brave://settings/socialBlocking</code> y activa el inicio de sesión de Google.</span>
                </div>
            </div>
        {/if}

        {#if isConnected}
            <div class="status-badge connected">
                <span class="dot"></span> Conectado como {userName || "Usuario"}
            </div>
            <button class="auth-btn disconnect" onclick={handleAuthAction} disabled={isLoading || !isGoogleReady}>
                {#if isLoading}
                    <span class="spinner"></span> Desconectando...
                {:else}
                    <IconLogout /> Desconectar GDrive
                {/if}
            </button>
        {:else}
            <div class="status-badge disconnected">
                <span class="dot"></span> No conectado
            </div>
            <button class="auth-btn connect" onclick={handleAuthAction} disabled={isLoading || !isGoogleReady}>
                {#if isLoading}
                    <span class="spinner"></span> Conectando...
                {:else}
                    <IconLogin /> Conectar GDrive
                {/if}
            </button>
        {/if}
    </div>
</div>

<style>
    #gdrive-auth-container { width: 100%; max-width: 640px; margin: 20px auto 0 auto; padding: 0 var(--padding); }
    .auth-card { background-color: #121212; border: 1px solid var(--input-border); border-radius: var(--border-radius); padding: 20px; display: flex; flex-direction: column; gap: 16px; }
    .card-header { display: flex; align-items: center; gap: 16px; }
    .card-header :global(.drive-logo) { width: 40px; height: 40px; color: #e9e9e9; }
    .header-text h3 { color: #ffffff; margin: 0; font-size: 16px; font-weight: 700; }
    .header-text p { color: var(--gray); margin: 2px 0 0 0; font-size: 13px; }
    
    .warning-box { display: flex; align-items: center; gap: 10px; background: rgba(207, 34, 46, 0.1); border: 1px solid rgba(207, 34, 46, 0.3); color: #ff6b6b; padding: 12px; border-radius: 8px; font-size: 12px; font-weight: 600; }
    .warning-box :global(svg) { width: 24px; height: 24px; flex-shrink: 0; }

    .status-badge { display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 600; padding: 6px 12px; border-radius: 20px; width: fit-content; }
    .dot { width: 8px; height: 8px; border-radius: 100%; }
    .status-badge.connected { background-color: rgba(52, 168, 83, 0.1); color: #81c995; }
    .status-badge.connected .dot { background-color: #34a853; }
    .status-badge.disconnected { background-color: rgba(255, 255, 255, 0.05); color: var(--gray); }
    .status-badge.disconnected .dot { background-color: var(--gray); }
    
    .auth-btn { width: 100%; display: flex; align-items: center; justify-content: center; gap: 10px; padding: 12px; border: none; border-radius: 8px; font-size: 14px; font-weight: 700; cursor: pointer; transition: background 0.2s, transform 0.1s; }
    .auth-btn:active:not(:disabled) { transform: scale(0.98); }
    .auth-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .auth-btn :global(svg) { width: 18px; height: 18px; }
    .auth-btn.connect { background-color: var(--primary); color: white; }
    .auth-btn.connect:hover:not(:disabled) { background-color: #be1e2d; }
    .auth-btn.disconnect { background-color: #27272a; color: #e9e9e9; }
    .auth-btn.disconnect:hover:not(:disabled) { background-color: #3f3f46; }
    .spinner { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-radius: 100%; border-top-color: white; animation: spin 0.8s linear infinite; }
    @keyframes spin { 100% { transform: rotate(360deg); } }
</style>